import type { Components, Theme } from "@mui/material/styles";
import type { StepIconProps } from "@mui/material/StepIcon";
import { RadiusToken } from "../../tokens/radii";

const STEP_ICON_SIZE = 16;
const STEP_ICON_BORDER_WIDTH = 2;

// Replaces MUI's default numbered SVG StepIcon with a plain dot whose
// fill/border is driven by `active` and `completed`. The dot reads color
// values from CSS custom properties set in the styleOverrides root below,
// matching the pattern used by MuiRadio.
const RmhStepIcon = ({
  active,
  completed,
  className,
}: StepIconProps): React.ReactElement => (
  <span
    className={[
      "RmhStepIcon",
      active && "RmhStepIcon-active",
      completed && "RmhStepIcon-completed",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    aria-hidden="true"
  />
);

export const MuiStepLabel: NonNullable<Components<Theme>["MuiStepLabel"]> = {
  defaultProps: {
    slots: { stepIcon: RmhStepIcon },
  },
  styleOverrides: {
    iconContainer: ({ theme }) => ({
      paddingRight: theme.spacing(2),
    }),
    root: ({ theme }) => {
      const rmh = theme.vars!.palette.rmh;
      return {
        "& .RmhStepIcon": {
          display: "inline-block",
          flexShrink: 0,
          width: STEP_ICON_SIZE,
          height: STEP_ICON_SIZE,
          borderRadius: RadiusToken.MAX,
          boxSizing: "border-box",
          borderStyle: "solid",
          borderWidth: STEP_ICON_BORDER_WIDTH,
          transition: theme.transitions.create(
            ["background-color", "border-color"],
            { duration: theme.transitions.duration.shortest }
          ),
          backgroundColor: rmh.paper["1-surface"].rest,
          borderColor: rmh.stateBorder.disabled,
        },
        "& .RmhStepIcon-active": {
          backgroundColor: rmh.paper["1-surface"].rest,
          borderColor: rmh.border["4-faint"].active,
        },
        "& .RmhStepIcon-completed": {
          backgroundColor: rmh.paper["5-brandFilled"].rest,
          borderColor: rmh.paper["5-brandFilled"].rest,
        },
        "& .MuiStepLabel-iconContainer": {
          padding: 0,
          [theme.breakpoints.up("md")]: { paddingRight: theme.spacing(2) },
        },
        "& .MuiStepLabel-label": {
          ...theme.typography.bodyMdMedium,
          color: rmh.text.secondary,
          display: "none",
          [theme.breakpoints.up("md")]: { display: "block" },
        },
        "& .MuiStepLabel-label.Mui-active": {
          fontWeight: 600,
          color: rmh.text.brand,
        },
        "& .MuiStepLabel-label.Mui-completed": {
          color: rmh.text.primary,
        },
      };
    },
  },
};
